import { Button, Tooltip, Modal, Input } from "antd";
import type { InputRef } from "antd";
import * as Styles from "./styles";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  resolveToteInputMode,
  TOTE_MANUAL_INPUT_MODE,
} from "../../../../../../common/utils/toteInputMode";
import RestartPopup from "../restart-popup/RestartPopup";
import FlagPopup from "../flag-popup/FlagPopup";
import { ReactComponent as ImageIcon } from "./icons/product.svg";
import { ReactComponent as ProductIcon } from "./icons/product.svg";
import { ReactComponent as LineWhiteIcon } from "./icons/lineWhite.svg";
import { ReactComponent as PickupWhiteIcon } from "./icons/pickupWhite.svg";
import { ReactComponent as ScanIcon } from "./icons/scan.svg";
import { ReactComponent as InfoIcon } from "./icons/info-icon.svg";
import { ReactComponent as TickIcon } from "./icons/tick-icon.svg";
import { ReactComponent as InfoIconWhite } from "./icons/info-icon-white.svg";
import { ReactComponent as LineIcon } from "./icons/line.svg";
import { ReactComponent as HoldInfoIcon } from "./icons/hold-info.svg";
import { ReactComponent as ProgressInfoIcon } from "./icons/progress-info.svg";
import { ReactComponent as ErrorIcon } from "./icons/info.svg";
import { formatDistance } from "date-fns";
import { message } from "antd";
import { ReactComponent as Toast } from "../../../../../../assets/icons/toast-icon.svg";
import { useLocation, useNavigate } from "react-router-dom";
import OkPopup from "../ok-popup/Okpopup";
import axios, { } from "../../../../../../common/api/axios";
import { formatSlotDisplay } from "../../../../../../common/utils/slot";
import {
  flattenStaticPickWallSlots,
} from "../../../../../../common/utils/staticPickWall";
import {
  getShortPickRemarks,
  isShortPick,
  SHORT_PICK_REASONS,
  ShortPickReason,
} from "../../../../../../common/utils/shortPick";
import {
  areOrderPicksDone,
  getReleaseCandidateOrderRefs,
} from "./putRelease";
import { async } from "q";
import { ReactComponent as LoadIcon } from "./icons/loader.svg";
import AuthenticatedImage from "../../../../../../common/components/AuthenticatedImage";
import { printerApiScope } from "../../../../../../common/auth/authConfig";

export interface SkuBinProps { pickItemsData?: any, pickStationsFirstData?: any, pickStationsSecondData?: any }

type PutWallGateState = {
  putWallId?: string;
  location?: string;
  mappedPtlDeviceId?: string;
  isDisabled?: boolean;
  currentOrder?: string;
  orderToteId?: string;
};

const isTruthyEnv = (value?: string) => {
  if (!value) {
    return false;
  }

  return ["true", "1", "yes", "on"].includes(value.trim().toLowerCase());
};

const isOrderToteMappingFeatureEnabled = () => {
  const env = process.env as Record<string, string | undefined>;
  return isTruthyEnv(env.REACT_APP_CARTE_PLUS_ENABLE_ORDER_TOTE_MAPPING);
};

const isPutWallFeatureEnabled = () => {
  const env = process.env as Record<string, string | undefined>;
  return isTruthyEnv(env.REACT_APP_CARTE_PLUS_ENABLE_PUT_WALL);
};

const normalizePutGate = (value?: string | null) => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return "";
  }

  return raw.replace(/^put[\s-]*/i, "").trim();
};

const formatPutGateLabel = (value?: string | null) => {
  const gate = normalizePutGate(value);
  return gate ? `PUT-${gate}` : "";
};

const toNumber = (value: any, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// Modal stacking for this screen. The put-wall release modal is a blocking interrupt — it
// has no close affordance at all (closable, maskClosable and keyboard are every one of them
// off), so nothing may ever render on top of it. Any modal added here must sit below that
// band, and antd's automatic z-index is not enough to guarantee it: sibling modals with the
// same z-index are stacked by DOM order, so a later-rendered one would cover the release.
const SHORT_PICK_MODAL_Z_INDEX = 1100;
const PUT_RELEASE_MODAL_Z_INDEX = 1200;

const formatQuantity = (value: any) => {
  const numericValue = toNumber(value);
  return numericValue < 10 ? `0${numericValue}`.slice(-2) : `${numericValue}`;
};

const getOrderReferenceId = (order: any) =>
  String(
    order?.orderReferenceId ??
      order?.orderReferenceID ??
      order?.orderNum ??
      order?.ordernum ??
      ""
  ).trim();

const sortGatesByLocation = (left: PutWallGateState, right: PutWallGateState) => {
  const leftGate = normalizePutGate(left.location);
  const rightGate = normalizePutGate(right.location);
  const leftNumber = Number(leftGate);
  const rightNumber = Number(rightGate);

  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }

  return leftGate.localeCompare(rightGate, undefined, { numeric: true });
};

export default function SkuBin({ pickItemsData, pickStationsFirstData, pickStationsSecondData }: SkuBinProps) {
  const BARCODE_BASE_URL: any = process.env.REACT_APP_BARCODE_URL;
  const enableReportProblem = process.env.REACT_APP_ENABLE_REPORT_PROBLEM === "true";
  const enablePutWallFeature = isPutWallFeatureEnabled();
  const enableOrderToteMapping = enablePutWallFeature && isOrderToteMappingFeatureEnabled();
  const orderToteMappingRefreshDuration =
    Number(process.env.REACT_APP_REFRESH_DURATION) > 0
      ? Number(process.env.REACT_APP_REFRESH_DURATION)
      : 5000;
  const operatorTasks = useMemo(
    () => (Array.isArray(pickItemsData) ? pickItemsData : []),
    [pickItemsData]
  );
  const pickTaskItems = useMemo(
    () => operatorTasks.filter((task: any) => task?.pickTask),
    [operatorTasks]
  );
  const location = useLocation();
  const navigate = useNavigate();
  const [orderItems, setOrderItems] = useState<any>([]);
  const [isRestartPopup, setIsRestartPopup] = useState(false);
  const [isFlagPopup, setIsFlagPopup] = useState(false);
  const [isOkPopup, setIsOkPopup] = useState(false);
  const [qCount, setQCount] = useState(0);
  const [allQty, setAllQty] = useState(0);
  const [doneLoading, setDoneLoading] = useState(false);
  const [okayLoading, setOkayLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [orderQuantity, setOrderQuantity] = useState<any>([]);
  const [itemsData, setItemsData] = useState<any>([]);
  const [state, setState] = useState("");

  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isFlagModalVisible, setIsFlagModalVisible] = useState(false);
  const [flagRemarks, setFlagRemarks] = useState("");
  const [flagStep, setFlagStep] = useState<"select" | "confirm">("select");
  const [flagReason, setFlagReason] = useState<"damaged" | "wrong" | null>(null);

  // Short pick: the operator confirmed fewer units than the task requires and must
  // pick a reason before the pick is recorded and signalled.
  const [isShortPickModalVisible, setIsShortPickModalVisible] = useState(false);
  const [shortPickReason, setShortPickReason] = useState<ShortPickReason | null>(null);
  const [shortPickSubmitting, setShortPickSubmitting] = useState(false);

  // Add new state for static picks
  const [staticSlots, setStaticSlots] = useState<any>([]);
  const [staticItemsData, setStaticItemsData] = useState<any>([]);

  const [binPartitionData, setBinPartitionData] = useState<any>({});
  const [isSkuModalVisible, setIsSkuModalVisible] = useState(false);

  const [putWallGates, setPutWallGates] = useState<any[]>([]);
  const [defaultPutWallId, setDefaultPutWallId] = useState("");
  const [fulfillmentOrders, setFulfillmentOrders] = useState<any[]>([]);
  const [lastFulfillmentId, setLastFulfillmentId] = useState<string | null>(null);
  const [orderToteMappingTasks, setOrderToteMappingTasks] = useState<any[]>([]);
  const [putTasks, setPutTasks] = useState<any[]>([]);
  const [completedMappingTaskIds, setCompletedMappingTaskIds] = useState<Set<string>>(new Set());
  const [selectedMappingTask, setSelectedMappingTask] = useState<any>(null);
  const [mappingGate, setMappingGate] = useState("");
  const [mappingOrderToteId, setMappingOrderToteId] = useState("");
  const [mappingLoading, setMappingLoading] = useState(false);
  // Order-tote-mapping field: auto-focused for scanning with the on-screen
  // keyboard suppressed, until the operator taps the field for manual entry.
  const [mappingToteFieldTapped, setMappingToteFieldTapped] = useState(false);
  const mappingToteInputRef = useRef<InputRef>(null);
  const [putReleaseTasks, setPutReleaseTasks] = useState<any[]>([]);
  const [putReleaseLoadingTaskIds, setPutReleaseLoadingTaskIds] = useState<Set<string>>(new Set());
  const putReleaseHandledRef = useRef<Set<string>>(new Set());
  const releasedOrderRefs = useRef<Set<string>>(new Set());
  const releaseInFlightRef = useRef<Set<string>>(new Set());
  const releaseModalOrderRefs = useRef<Set<string>>(new Set());
  // Guards against overlapping async release sweeps (each sweep issues one
  // /Wms/{ref} GET per candidate order; the ~5s putTasks poll re-triggers it).
  const sweepInFlightRef = useRef(false);
  const completedPickTaskIdsRef = useRef<Set<string>>(new Set());
  const latestPickTaskItemsRef = useRef<any[]>([]);
  const latestItemsDataRef = useRef<any[]>([]);
  const latestStaticItemsDataRef = useRef<any[]>([]);
  const latestPutWallEnabledRef = useRef(false);
  
  // Track polling timers per pick task id
  const pollTimersRef = useRef<Record<string, any>>({});

  // put wall flag
  const [isPutWallEnabled, setIsPutWallEnabled] = useState(false);

  const GroupColor: Record<string, { bg: string; border: string; text: string }> = {
    "1": { bg: "#ffd6d6", border: "#ff4d4f", text: "#a8071a" }, // red
    "2": { bg: "#d6e4ff", border: "#2f54eb", text: "#10239e" }, // blue
    "3": { bg: "#fbd6ff", border: "#b60fef", text: "#90109e" }, // magenta
    "4": { bg: "#d9f7ff", border: "#13c2c2", text: "#006d75" }, // cyan
  };

  const DefaultHighlightColor = { bg: "#c2c2c2", border: "#8c8c8c", text: "#333" };

  const [ackPutWall, setAckPutWall] = useState<string | null>(null);
  // track order id to prevent phantom picks for the same order id
  const [blockedOrderId, setBlockedOrderId] = useState<string | null>(null);

  useEffect(() => {
    latestPickTaskItemsRef.current = pickTaskItems;
  }, [pickTaskItems]);

  useEffect(() => {
    latestItemsDataRef.current = itemsData;
  }, [itemsData]);

  useEffect(() => {
    latestStaticItemsDataRef.current = staticItemsData;
  }, [staticItemsData]);

  useEffect(() => {
    latestPutWallEnabledRef.current = isPutWallEnabled;
  }, [isPutWallEnabled]);

  const visibleOrderToteMappingTasks = useMemo(
    () =>
      orderToteMappingTasks.filter(
        (task: any) =>
          task?.id &&
          task?.orderToteMappingTask &&
          task?.isComplete !== true &&
          task?.isCancelled !== true &&
          task?.isAborted !== true &&
          !completedMappingTaskIds.has(task.id)
      ),
    [orderToteMappingTasks, completedMappingTaskIds]
  );

  const sortedPutWallGates = useMemo(
    () => putWallGates.slice().sort(sortGatesByLocation),
    [putWallGates]
  );

  const visibleMappingTasksByGate = useMemo(() => {
    const mappingTasks = new Map<string, any>();
    visibleOrderToteMappingTasks.forEach((task: any) => {
      const gate = normalizePutGate(task?.orderToteMappingTask?.gate);
      if (gate && !mappingTasks.has(gate)) {
        mappingTasks.set(gate, task);
      }
    });
    return mappingTasks;
  }, [visibleOrderToteMappingTasks]);

  const getPutWallGateForOrder = (orderReferenceId?: string | null) => {
    const orderId = String(orderReferenceId ?? "").trim();
    if (!orderId) {
      return undefined;
    }

    return sortedPutWallGates.find(
      (gate: PutWallGateState) => String(gate?.currentOrder ?? "").trim() === orderId
    );
  };

  const getMappingTaskForOrder = (orderReferenceId?: string | null) => {
    const orderId = String(orderReferenceId ?? "").trim();
    if (!orderId) {
      return undefined;
    }

    return visibleOrderToteMappingTasks.find(
      (task: any) =>
        String(task?.orderToteMappingTask?.orderReferenceId ?? "").trim() === orderId
    );
  };

  const fetchFulfillmentOrdersForCurrentFulfillment = useCallback(async () => {
    const fulfillmentId = pickTaskItems.length > 0
      ? pickTaskItems[0]?.fulfillmentId
      : lastFulfillmentId;

    if (!fulfillmentId) {
      return [];
    }

    try {
      const res = await axios.get(`/Wms/byFulfillmentId/${fulfillmentId}`, {
        headers: { Accept: "application/json" },
      });
      if (res.status === 200 && Array.isArray(res.data)) {
        setFulfillmentOrders(res.data);
        setLastFulfillmentId(fulfillmentId);
        return res.data;
      }
    } catch (_e) {
      // ignore
    }

    return [];
  }, [lastFulfillmentId, pickTaskItems]);

  const fetchWmsOrder = async (orderReferenceId: string) => {
    if (!orderReferenceId) {
      return null;
    }

    try {
      const res = await axios.get(`/Wms/${orderReferenceId}`, {
        headers: { Accept: "application/json" },
      });
      return res.status === 200 ? res.data : null;
    } catch (_e) {
      return null;
    }
  };

  const areOrderPicksDoneFromWms = async (orderReferenceId: string) => {
    const order = await fetchWmsOrder(orderReferenceId);
    return areOrderPicksDone(order);
  };

  const getGateColorKeyForPickItem = (item: any) => {
    const groupGate = normalizePutGate(item?.pickTask?.group);
    if (groupGate) {
      return groupGate;
    }

    const orderReferenceId = item?.pickTask?.orderReferenceId;
    const putWallGate = getPutWallGateForOrder(orderReferenceId);
    if (putWallGate?.location) {
      return normalizePutGate(putWallGate.location);
    }

    const mappingTask = getMappingTaskForOrder(orderReferenceId);
    return normalizePutGate(mappingTask?.orderToteMappingTask?.gate);
  };

  const isActivePutTask = useCallback((task: any) =>
    task?.id &&
    task?.isComplete !== true &&
    task?.completedStatus !== true &&
    task?.putTask?.isComplete !== true &&
    task?.putTask?.completedStatus !== true &&
    task?.isCancelled !== true &&
    task?.isAborted !== true &&
    !putReleaseHandledRef.current.has(task.id), []);

  const getPutTaskForOrder = (orderReferenceId: string, tasks = putTasks) =>
    tasks.find(
      (task: any) =>
        task?.putTask?.orderReferenceId === orderReferenceId &&
        isActivePutTask(task)
    );

  const addPutReleaseTask = (putTask: any) => {
    const putTaskId = putTask?.id;
    const orderReferenceId = putTask?.putTask?.orderReferenceId;

    if (!putTaskId || !orderReferenceId) {
      return;
    }

    releaseModalOrderRefs.current.add(orderReferenceId);
    setPutReleaseTasks((current: any[]) => {
      const alreadyOpen = current.some(
        (task: any) =>
          task?.id === putTaskId ||
          task?.putTask?.orderReferenceId === orderReferenceId
      );

      return alreadyOpen ? current : [...current, putTask];
    });
  };

  const fetchPutTasks = useCallback(async () => {
    try {
      const res = await axios.get("/PickStation/putTasks", {
        headers: {
          Accept: "application/json",
          tenantId: 1,
        },
      });

      if (res.status === 200 && Array.isArray(res.data)) {
        const activePutTasks = res.data.filter(isActivePutTask);
        setPutTasks(activePutTasks);
        return activePutTasks;
      }
    } catch (_error) {
      setPutTasks([]);
    }

    return [];
  }, [isActivePutTask]);

  const completeOrderInUi = (orderReferenceId: string) => {
    releaseModalOrderRefs.current.delete(orderReferenceId);
    setBlockedOrderId(orderReferenceId);
    setFulfillmentOrders((prev) =>
      prev.filter((o: any) => getOrderReferenceId(o) !== orderReferenceId)
    );
    setItemsData((current: any[]) => {
      const next = current.filter(
        (item: any) => item?.pickTask?.orderReferenceId !== orderReferenceId
      );
      latestItemsDataRef.current = next;
      return next;
    });
    setStaticItemsData((current: any[]) => {
      const next = current.filter(
        (item: any) => item?.pickTask?.orderReferenceId !== orderReferenceId
      );
      latestStaticItemsDataRef.current = next;
      return next;
    });
    setOrderItems((current: any[]) =>
      current.filter(
        (item: any) => item?.pickTask?.orderReferenceId !== orderReferenceId
      )
    );
    setSelectedOrder((current: any) =>
      current?.pickTask?.orderReferenceId === orderReferenceId ? [] : current
    );
    setAllQty(0);
  };

  const openPutReleaseModalForOrder = async (orderReferenceId: string) => {
    if (!latestPutWallEnabledRef.current || releasedOrderRefs.current.has(orderReferenceId)) {
      return false;
    }

    const latestPutTasks = await fetchPutTasks();
    const putTask =
      getPutTaskForOrder(orderReferenceId, latestPutTasks) ||
      getPutTaskForOrder(orderReferenceId);

    if (putTask && !putReleaseHandledRef.current.has(putTask.id)) {
      addPutReleaseTask(putTask);
      return true;
    }

    if (!getPutWallGateForOrder(orderReferenceId)) {
      completeOrderInUi(orderReferenceId);
    }

    return false;
  };

  const getKnownPickTask = (taskId: string, fallbackTask?: any) => {
    if (fallbackTask?.pickTask) {
      return fallbackTask;
    }

    return [
      ...latestPickTaskItemsRef.current,
      ...latestItemsDataRef.current,
      ...latestStaticItemsDataRef.current,
    ].find((item: any) => item?.id === taskId);
  };

  const markPolledTaskCompleteInUi = (taskId: string) => {
    completedPickTaskIdsRef.current.add(taskId);

    setItemsData((current: any[]) => {
      const next = current.map((obj: any) =>
        obj.id === taskId
          ? { ...obj, orderColor: "completed", completedStatus: true, okLoader: false }
          : obj
      );
      latestItemsDataRef.current = next;
      return next;
    });

    setOrderItems((current: any[]) =>
      current.map((obj: any) =>
        obj.id === taskId
          ? { ...obj, orderColor: "completed", completedStatus: true, okLoader: false }
          : obj
      )
    );

    setStaticItemsData((current: any[]) => {
      const next = current.map((obj: any) =>
        obj.id === taskId
          ? { ...obj, orderColor: "completed", completedStatus: true, okLoader: false }
          : obj
      );
      latestStaticItemsDataRef.current = next;
      return next;
    });
  };

  const openPutReleaseModalWhenOrderReady = async (completedPickItem: any) => {
    const orderRefId: string | undefined =
      completedPickItem?.pickTask?.orderReferenceId;
    if (!orderRefId) {
      return false;
    }

    const arePicksDone = await areOrderPicksDoneFromWms(orderRefId);

    if (!arePicksDone || !latestPutWallEnabledRef.current) {
      return false;
    }

    if (releasedOrderRefs.current.has(orderRefId)) {
      return false;
    }

    if (releaseModalOrderRefs.current.has(orderRefId)) {
      return true;
    }

    releaseModalOrderRefs.current.add(orderRefId);
    const releaseModalOpened = await openPutReleaseModalForOrder(orderRefId);
    if (!releaseModalOpened) {
      releaseModalOrderRefs.current.delete(orderRefId);
    }

    return releaseModalOpened;
  };

  const isPutReleaseTaskLoading = (putTaskId?: string) =>
    !!putTaskId && putReleaseLoadingTaskIds.has(putTaskId);

  const isOperatorTaskComplete = async (taskId: string) => {
    try {
      const res = await axios.get(`/PickStation/task/${taskId}`, {
        headers: {
          Accept: "application/json",
          tenantId: 1,
        },
      });

      return res?.status === 200 && res?.data?.isComplete === true;
    } catch (_error) {
      return false;
    }
  };

  const closePutReleaseForTask = (putTaskId: string, orderReferenceId: string) => {
    releaseModalOrderRefs.current.delete(orderReferenceId);
    setPutReleaseTasks((current: any[]) =>
      current.filter((task: any) => task?.id !== putTaskId)
    );
  };

  const completePutReleaseInUi = async (
    putTaskId: string,
    orderReferenceId: string
  ) => {
    closePutReleaseForTask(putTaskId, orderReferenceId);

    if (putReleaseHandledRef.current.has(putTaskId)) {
      return;
    }

    putReleaseHandledRef.current.add(putTaskId);
    releasedOrderRefs.current.add(orderReferenceId);
    setPutTasks((current) =>
      current.filter((task: any) => task?.id !== putTaskId)
    );
    messageApi.open({
      content: `Order ${orderReferenceId} released`,
      icon: <Toast />,
      className: "order",
      style: {
        marginTop: "85vh",
      },
    });
    completeOrderInUi(orderReferenceId);
    await refreshFulfillmentOrders();
    await fetchPutWalls();
  };

  const handleConfirmPutRelease = async (putReleaseTask: any) => {
    const putTaskId = putReleaseTask?.id;
    const orderReferenceId = putReleaseTask?.putTask?.orderReferenceId;

    if (
      !putTaskId ||
      !orderReferenceId ||
      releaseInFlightRef.current.has(putTaskId)
    ) {
      return;
    }

    try {
      releaseInFlightRef.current.add(putTaskId);
      setPutReleaseLoadingTaskIds((current) => new Set(current).add(putTaskId));
      await axios.put(`/PickStation/signalPut/${putTaskId}`, null, {
        headers: {
          Accept: "application/json",
          tenantId: 1,
        },
      });
      closePutReleaseForTask(putTaskId, orderReferenceId);
      await completePutReleaseInUi(putTaskId, orderReferenceId);
    } catch (error: any) {
      if (await isOperatorTaskComplete(putTaskId)) {
        await completePutReleaseInUi(putTaskId, orderReferenceId);
        return;
      }

      messageApi.open({
        type: "error",
        content:
          typeof error?.response?.data === "string"
            ? error.response.data
            : "Failed to release put wall",
        icon: <Toast />,
        className: "error",
        style: {
          marginTop: "85vh",
        },
      });
    } finally {
      releaseInFlightRef.current.delete(putTaskId);
      setPutReleaseLoadingTaskIds((current) => {
        const next = new Set(current);
        next.delete(putTaskId);
        return next;
      });
    }
  };

  useEffect(() => {
    if (putReleaseTasks.length === 0) {
      return;
    }

    let isActive = true;
    const pollReleaseTasks = async () => {
      if (!isActive) {
        return;
      }

      await Promise.all(
        putReleaseTasks.map(async (putReleaseTask: any) => {
          const putTaskId = putReleaseTask?.id;
          const orderReferenceId = putReleaseTask?.putTask?.orderReferenceId;

          if (!putTaskId || !orderReferenceId) {
            return;
          }

          if (putReleaseHandledRef.current.has(putTaskId)) {
            closePutReleaseForTask(putTaskId, orderReferenceId);
            return;
          }

          const isComplete = await isOperatorTaskComplete(putTaskId);
          if (isActive && isComplete) {
            await completePutReleaseInUi(putTaskId, orderReferenceId);
          }
        })
      );
    };

    pollReleaseTasks();
    const interval = setInterval(
      pollReleaseTasks,
      orderToteMappingRefreshDuration
    );

    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [
    putReleaseTasks,
    orderToteMappingRefreshDuration,
  ]);

  useEffect(() => {
    if (!enableOrderToteMapping && !isPutWallEnabled) {
      setPutTasks([]);
      return;
    }

    fetchPutTasks();
    const interval = setInterval(fetchPutTasks, orderToteMappingRefreshDuration);

    return () => {
      clearInterval(interval);
    };
  }, [
    enableOrderToteMapping,
    fetchPutTasks,
    isPutWallEnabled,
    orderToteMappingRefreshDuration,
  ]);

  // Server-driven "Confirm Release" safety net.
  //
  // Candidates are derived from the polled putTasks (server truth) rather than
  // a session-local acked set, so an orchestrator restart mid-fulfillment (new
  // fulfillment id, no fresh pick events) or a page refresh still surfaces the
  // release modal for orders whose picks are done. The event fast paths
  // (OK-tap, per-task completion poll, flag flow) still open the modal
  // immediately; this sweep only *adds* modals the old code could never reach.
  // See Docs/Internal/operator-ui-orchestrator-restart-resilience-plan.md §4.
  useEffect(() => {
    if (!isPutWallEnabled || putTasks.length === 0) {
      return;
    }

    let isActive = true;

    const openReadyReleaseModals = async () => {
      if (sweepInFlightRef.current) {
        return;
      }
      sweepInFlightRef.current = true;
      try {
        const candidateOrderRefs = getReleaseCandidateOrderRefs(
          putTasks,
          isActivePutTask
        );
        for (const orderReferenceId of candidateOrderRefs) {
          if (!isActive) {
            return;
          }
          if (releaseModalOrderRefs.current.has(orderReferenceId)) {
            continue;
          }
          if (releasedOrderRefs.current.has(orderReferenceId)) {
            continue;
          }
          if (!(await areOrderPicksDoneFromWms(orderReferenceId))) {
            continue;
          }

          releaseModalOrderRefs.current.add(orderReferenceId);
          const opened = await openPutReleaseModalForOrder(orderReferenceId);
          if (!opened) {
            releaseModalOrderRefs.current.delete(orderReferenceId);
          }
        }
      } finally {
        sweepInFlightRef.current = false;
      }
    };

    openReadyReleaseModals();

    return () => {
      isActive = false;
    };
  }, [isPutWallEnabled, putTasks]);

  const fetchOrderToteMappingTasks = useCallback(async () => {
    if (!enableOrderToteMapping) {
      setOrderToteMappingTasks([]);
      return;
    }

    try {
      const res = await axios.get("PickStation/orderToteMappingTasks", {
        headers: {
          Accept: "application/json",
          tenantId: 1,
        },
      });

      if (res.status === 200 && Array.isArray(res.data)) {
        setOrderToteMappingTasks(res.data);
      }
    } catch (_error) {
      setOrderToteMappingTasks([]);
    }
  }, [enableOrderToteMapping]);

  useEffect(() => {
    if (!enableOrderToteMapping) {
      setOrderToteMappingTasks([]);
      return;
    }

    fetchOrderToteMappingTasks();
    const interval = setInterval(
      fetchOrderToteMappingTasks,
      orderToteMappingRefreshDuration
    );

    return () => {
      clearInterval(interval);
    };
  }, [
    enableOrderToteMapping,
    fetchOrderToteMappingTasks,
    orderToteMappingRefreshDuration,
  ]);

  useEffect(() => {
    if (isSkuModalVisible) {
      setAckPutWall(null);
    }
  }, [isSkuModalVisible]);

  // The pick vanished from the poll while the operator was choosing a short-pick reason
  // (order finished elsewhere, orchestrator restart). Drop the decision rather than leave
  // invisible state behind that would keep blocking handleCloseSkuModal.
  useEffect(() => {
    if (isShortPickModalVisible && !selectedOrder?.id) {
      setIsShortPickModalVisible(false);
      setShortPickReason(null);
    }
  }, [isShortPickModalVisible, selectedOrder]);

  const openOrderToteMappingModal = (task: any) => {
    setMappingToteFieldTapped(false);
    setSelectedMappingTask(task);
    setMappingGate(formatPutGateLabel(task?.orderToteMappingTask?.gate));
    setMappingOrderToteId(task?.orderToteMappingTask?.orderToteId ?? "");
  };

  const closeOrderToteMappingModal = () => {
    if (mappingLoading) {
      return;
    }

    setSelectedMappingTask(null);
    setMappingGate("");
    setMappingOrderToteId("");
    setMappingToteFieldTapped(false);
  };

  // Auto-focus the tote field when the mapping modal opens so a scanner can
  // enter the tote ID immediately. The keyboard stays hidden (inputMode="none")
  // until the operator taps the field. Text is pre-selected so a scan overwrites
  // any existing value.
  useEffect(() => {
    if (!selectedMappingTask) {
      return;
    }

    const timer = setTimeout(() => {
      mappingToteInputRef.current?.focus({ cursor: "all" });
    }, 300);

    return () => clearTimeout(timer);
  }, [selectedMappingTask]);

  // An explicit tap switches the field to manual entry and reveals the on-screen
  // keyboard. The DOM inputMode is flipped synchronously within the tap gesture
  // so the keyboard appears on this tap rather than the next one.
  const handleMappingToteFieldTap = () => {
    if (mappingToteFieldTapped) {
      return;
    }

    const inputEl = mappingToteInputRef.current?.input;
    if (inputEl) {
      inputEl.inputMode = TOTE_MANUAL_INPUT_MODE;
    }

    setMappingToteFieldTapped(true);
  };

  const fetchPutWalls = useCallback(async () => {
    if (!enablePutWallFeature) {
      setDefaultPutWallId("");
      setPutWallGates([]);
      setIsPutWallEnabled(false);
      return [];
    }

    try {
      const res = await axios.get("/PutWall", {
        headers: {
          Accept: "application/json",
        },
      });

      if (res.status === 200 && Array.isArray(res.data)) {
        const putwalls = res.data as any[];
        const gates = putwalls.flatMap((putwall: any) =>
          Array.isArray(putwall?.gates)
            ? putwall.gates.map((gate: any) => ({
                ...gate,
                putWallId: gate?.putWallId ?? putwall?.putWallId ?? putwall?.id ?? "",
              }))
            : []
        );

        setDefaultPutWallId(putwalls[0]?.putWallId ?? putwalls[0]?.id ?? "");
        setPutWallGates(gates);
        setIsPutWallEnabled(gates.length > 0);
        return gates;
      }
    } catch (error) {
      console.error("Error fetching put walls:", error);
    }

    setDefaultPutWallId("");
    setPutWallGates([]);
    setIsPutWallEnabled(false);
    return [];
  }, [enablePutWallFeature]);

  useEffect(() => {
    fetchPutWalls();
    const interval = setInterval(fetchPutWalls, orderToteMappingRefreshDuration);

    return () => {
      clearInterval(interval);
    };
  }, [fetchPutWalls, orderToteMappingRefreshDuration]);

  const handleInitializeOrderToteMapping = async () => {
    const taskId = selectedMappingTask?.id;
    const orderReferenceId =
      selectedMappingTask?.orderToteMappingTask?.orderReferenceId ?? "";
    const putWallId =
      selectedMappingTask?.orderToteMappingTask?.putWallId ?? defaultPutWallId;
    const gate = normalizePutGate(
      selectedMappingTask?.orderToteMappingTask?.gate ?? mappingGate
    );
    const orderToteId = mappingOrderToteId.trim();

    if (!orderToteId) {
      messageApi.open({
        type: "error",
        content: "Order tote ID is required",
        icon: <Toast />,
        className: "error",
        style: {
          marginTop: "85vh",
        },
      });
      return;
    }

    if (!taskId || !orderReferenceId || !putWallId || !gate) {
      messageApi.open({
        type: "error",
        content: "Mapping task is missing put wall or gate",
        icon: <Toast />,
        className: "error",
        style: {
          marginTop: "85vh",
        },
      });
      return;
    }

    try {
      setMappingLoading(true);
      await axios.put(
        `/PickStation/updateOrderToteMappingTask/${encodeURIComponent(
          taskId
        )}/${encodeURIComponent(putWallId)}/${encodeURIComponent(
          gate
        )}/${encodeURIComponent(orderToteId)}`,
        null,
        {
          headers: {
            Accept: "application/json",
            tenantId: 1,
          },
        }
      );

      setCompletedMappingTaskIds((current) => {
        const next = new Set(current);
        next.add(taskId);
        return next;
      });
      setOrderToteMappingTasks((current) =>
        current.filter((task: any) => task?.id !== taskId)
      );
      messageApi.open({
        content: "Order tote mapping initialized",
        icon: <Toast />,
        className: "order",
        style: {
          marginTop: "85vh",
        },
      });
      setSelectedMappingTask(null);
      setMappingGate("");
      setMappingOrderToteId("");
      await fetchPutWalls();
      await fetchOrderToteMappingTasks();
    } catch (error: any) {
      messageApi.open({
        type: "error",
        content:
          typeof error?.response?.data === "string"
            ? error.response.data
            : "Failed to initialize order tote mapping",
        icon: <Toast />,
        className: "error",
        style: {
          marginTop: "85vh",
        },
      });
    } finally {
      setMappingLoading(false);
    }
  };

  const totalOrderCount = orderItems.reduce(
    (total: any, currentValue: any) =>
      (total = total + currentValue.pickTask.quantity),
    0
  ) + staticItemsData.reduce(
    (total: any, currentValue: any) =>
      (total = total + currentValue.pickTask.quantity),
    0
  );
  /* const firstRow = ["5,4,2", "4,4,2", "3,4,2", "2,4,2", "1,4,2"]; 
  const secondRow = ["5,4,1", "4,4,1", "3,4,1", "2,4,1", "1,4,1"]; */
  const [firstRow, setFirstRow] = useState<any[]>(pickStationsFirstData || []);
  const [secondRow, setSecondRow] = useState<any[]>(pickStationsSecondData || []);

  
  useEffect(() => {
    setFirstRow(pickStationsFirstData || []);
    setSecondRow(pickStationsSecondData || []);
  }, [pickStationsFirstData, pickStationsSecondData]);


  useEffect(() => {
    const fulfillmentId = pickTaskItems.length > 0 ? pickTaskItems[0]?.fulfillmentId : null;
    if (fulfillmentId && fulfillmentId !== lastFulfillmentId) {
      (async () => {
        try {
          const res = await axios.get(`/Wms/byFulfillmentId/${fulfillmentId}`, { headers: { Accept: "application/json" } });
          if (res.status === 200 && Array.isArray(res.data)) {
            setFulfillmentOrders(res.data);
            setLastFulfillmentId(fulfillmentId);
          }
        } catch (_e) {
          // ignore
        }
      })();
    }
  }, [pickTaskItems, lastFulfillmentId]);

  const getActiveOrderId = (): string | null => {
    if ((selectedOrder as any)?.pickTask?.orderReferenceId) {
      return (selectedOrder as any).pickTask.orderReferenceId as string;
    }
    if (pickTaskItems.length > 0) {
      return pickTaskItems[0]?.pickTask?.orderReferenceId ?? null;
    }
    return null;
  };

  const getVisibleSlotItem = (slotItems: any[]) => {
    if (!Array.isArray(slotItems) || slotItems.length === 0) {
      return null;
    }

    return slotItems.find((item: any) => item.orderColor !== "completed") ?? slotItems[0];
  };

  const stopPollingForTask = (taskId: string) => {
    const timers = pollTimersRef.current;
    const timer = timers[taskId];
    if (timer) {
      clearInterval(timer);
      delete timers[taskId];
    }
  };

  
  const startPollingForTask = (taskId: string) => {
    if (!taskId) return;
    const timers = pollTimersRef.current;
    if (timers[taskId]) return;

    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`/PickStation/task/${taskId}`, {
          headers: { Accept: "application/json", tenantId: 1 },
        });
        if (res?.status === 200 && res?.data) {
          const isComplete = res.data?.isComplete === true;
          if (isComplete) {
            const completedPickItem = getKnownPickTask(taskId, res.data);
            markPolledTaskCompleteInUi(taskId);
            stopPollingForTask(taskId);
            await openPutReleaseModalWhenOrderReady(completedPickItem);
          }
        }
      } catch (_e) {
        // ignore errors
      }
    }, 1500);

    timers[taskId] = interval;
  };

  // Start polling
  useEffect(() => {
    const receivedIds: string[] = pickTaskItems
      .map((p: any) => p?.id)
      .filter((x: any) => typeof x === "string");

    receivedIds.forEach((id) => startPollingForTask(id));

    // Cleanup all timers
    return () => {
      Object.keys(pollTimersRef.current).forEach(stopPollingForTask);
    };
  }, [pickTaskItems]);

  const refreshFulfillmentOrders = async () => {
    await fetchFulfillmentOrdersForCurrentFulfillment();
  };

  const fetchBinPartitionData = async (binId: string) => {
    try {
      const response = await axios.get(`/BinMaster/getById/${binId}`, {
        headers: {
          Accept: "application/json",
          tenantId: 1,
        },
      });
      if (response.status === 200) {
        setBinPartitionData((prev: any) => ({
          ...prev,
          [binId]: response.data
        }));
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching bin partition data:', error);
      return null;
    }
  };

  // Fetch static slots once
  useEffect(() => {
    const fetchStaticSlots = async () => {
      try {
        const response = await axios.get("/StaticPickWall", {
          headers: {
            Accept: "application/json",
            tenantId: 1,
          },
        });
        if (response.status === 200) {
          setStaticSlots(flattenStaticPickWallSlots(response.data));
        }
      } catch (error) {
        console.error('Error fetching static slots:', error);
        setStaticSlots([]);
      }
    };

    fetchStaticSlots();
  }, []);

	  useEffect(() => {
	    const incomingPickItems = pickTaskItems;

	    if (incomingPickItems.length === 0) {
	      latestItemsDataRef.current = [];
	      latestStaticItemsDataRef.current = [];
	      setItemsData([]);
	      setStaticItemsData([]);
	      setOrderItems([]);
	      setBlockedOrderId(null);
	      setAllQty(0);
	      if (isSkuModalVisible) {
	        setIsSkuModalVisible(false);
	      }
	      setSelectedOrder([]);
	      return;
	    }

	    if (blockedOrderId) {
	      const hasBlockedOrderItems = incomingPickItems.some(
        (item: any) => item?.pickTask?.orderReferenceId === blockedOrderId
      );

      if (!hasBlockedOrderItems) {
        setBlockedOrderId(null);
      }
    }

    const dataToProcess = blockedOrderId
      ? incomingPickItems.filter(
          (item: any) => item?.pickTask?.orderReferenceId !== blockedOrderId
        )
      : incomingPickItems;

    // Check if we need to update by comparing task IDs
    const currentTaskIds = [...itemsData, ...staticItemsData].map((item: any) => item.id).sort();
    const newTaskIds = dataToProcess.map((item: any) => item.id).sort();
    const hasNewTasks = currentTaskIds.length !== newTaskIds.length || 
                       currentTaskIds.some((id, index) => id !== newTaskIds[index]);

    if (dataToProcess.length !== 0 && hasNewTasks) {
      const regularItems: any = [];
      const staticItems: any = [];

      dataToProcess.forEach((val: any) => {
        const orderPlace = Object.values(val.pickTask.gate);
        const joinedItem = orderPlace.join(",");

        // Check if item matches regular pickup slots (firstRow/secondRow)
        const matchesRegularSlot = [...firstRow, ...secondRow].includes(joinedItem);
        
        // Check if item matches static slots based on x,y,z coordinates
        const matchesStaticSlot = staticSlots.find((slot: any) => 
          slot.x === val.pickTask.gate.x && 
          slot.y === val.pickTask.gate.y && 
          slot.z === val.pickTask.gate.z
        );

        const qty = toNumber(val.pickTask.quantity);

        // Check if this item already exists in current data to preserve its state
        const existingRegularItem = itemsData.find((item: any) => item.id === val.id);
        const existingStaticItem = staticItemsData.find((item: any) => item.id === val.id);
        const existingItem = existingRegularItem || existingStaticItem;

        // If this is a new task for an existing position, reset the state
        const existingPositionItem = [...itemsData, ...staticItemsData].find(
          (item: any) => item.position === joinedItem && item.id !== val.id
        );
        const shouldResetState = !!existingPositionItem;

        // If existing item is completed, force reset for next incoming task even if id matches
        const isExistingCompleted = !!(existingItem && existingItem.orderColor === "completed");

        const processedItem = {
          ...val,
          position: joinedItem,
          // If the existing was completed, reset to default for the new task
          orderColor: isExistingCompleted
            ? "default"
            : ((existingItem && !shouldResetState) ? existingItem.orderColor : "default"),
          selectedQty: isExistingCompleted
            ? 0
            : ((existingItem && !shouldResetState) ? toNumber(existingItem.selectedQty) : 0),
          completedStatus: isExistingCompleted
            ? false
            : ((existingItem && !shouldResetState) ? existingItem.completedStatus : false),
          okLoader: isExistingCompleted
            ? false
            : ((existingItem && !shouldResetState) ? existingItem.okLoader : false),
          qty: qty,
          skuDetails: (existingItem && !shouldResetState) ? existingItem.skuDetails : [],
          selectedPartition: isExistingCompleted
            ? null
            : ((existingItem && !shouldResetState) ? existingItem.selectedPartition : null)
        };

        // skip for static items
        if (val.pickTask.binId && !binPartitionData[val.pickTask.binId] && !matchesStaticSlot) {
          fetchBinPartitionData(val.pickTask.binId);
        }

        // Assign to appropriate section
        if (matchesRegularSlot) {
          regularItems.push(processedItem);
        } else if (matchesStaticSlot) {
          staticItems.push({
            ...processedItem,
            staticSlotId: matchesStaticSlot.id,
            staticPosition: `${matchesStaticSlot.x},${matchesStaticSlot.y},${matchesStaticSlot.z}`
          });
        }

      });

      // Set regular items
      setItemsData(regularItems);
      setOrderItems(regularItems);
      
      // Set static items
      setStaticItemsData(staticItems);
      
      // Reset selected order if the selected task no longer exists
      if (!selectedOrder || !selectedOrder.id) {
        if (!isSkuModalVisible) {
          setSelectedOrder([]);
        }
      } else {
        const allNewItems = [...regularItems, ...staticItems];
        const stillExists = allNewItems.find(item => item.id === selectedOrder.id);
        
        if (!stillExists) {
          setIsSkuModalVisible(false);
          setSelectedOrder([]);
        }
      }
    } else {
      // Do not clear slots when API returns empty
    }
    
  }, [pickTaskItems, staticSlots, firstRow, secondRow, blockedOrderId, isSkuModalVisible]);

  // POST Pick Items for order fulfillment
  const onHandleDoneButton = () => {
    setDoneLoading(true);
    //setTimeout(() => {
    setDoneLoading(false);
    messageApi.open({
      content: "Pick-task Complete !",
      icon: <Toast />,
      className: "order",
      style: {
        marginTop: "85vh",
      },
    });
    //}, 2000);
    /* setTimeout(() => {
      console.log('data---')
      navigate("/order-id");
    }, 1000); */
  };

  const markTaskCompleteInUi = (taskId: string, isStatic: boolean = false) => {
    completedPickTaskIdsRef.current.add(taskId);
    const targetItemsData = isStatic ? staticItemsData : itemsData;
    const updatedData = targetItemsData.map((obj: any) => {
      if (obj.id === taskId) {
        return {
          ...obj,
          orderColor: "completed",
          completedStatus: true,
          okLoader: false,
          selectedPartition: obj.selectedPartition,
        };
      }
      return obj;
    });

    if (isStatic) {
      latestStaticItemsDataRef.current = updatedData;
      setStaticItemsData(updatedData);
    } else {
      latestItemsDataRef.current = updatedData;
      setItemsData(updatedData);
      setOrderItems(updatedData);
    }

    setSelectedOrder([]);
    setIsSkuModalVisible(false);
  };

  //Each item for Ok button api
  // `pickedQuantityOverride` is set by the short-pick flow so the running total counts
  // what was actually picked rather than what the task asked for.
  const ohHandleOkItem = async (
    id: string,
    Index: any,
    isStatic: boolean = false,
    pickedQuantityOverride?: number
  ) => {

    const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
    const PICK_TOAST_MS = 1500;
    const ORDER_TOAST_MS = 1500;
    const targetItemsData = isStatic ? staticItemsData : itemsData;
    const setTargetItemsData = isStatic ? setStaticItemsData : setItemsData;
    const completedPickItem =
      targetItemsData.find((item: any) => item.id === id) || Index?.[0];
    const orderRefId: string | undefined =
      completedPickItem?.pickTask?.orderReferenceId;

    setTargetItemsData((current: any) =>
      targetItemsData.map((obj: any, key: any) => {
        if (obj.id === id) {
          return {
            ...obj,
            okLoader: true,
          };
        }
        return obj;
      })
    );
    setOkayLoading(true);
    setSelectedOrder(Index[0]);
    setSelectedTaskId(Index[0].id);
    setSelectedDate(Index[0].creationDate);

    try {
      const res = await axios.put(`/PickStation/signalPick/${id}`, null, {
        headers: {
          Accept: "application/json",
          tenantId: 1,
        },
      });

      if (res.status === 200) {
        messageApi.open({
          content: "Pick-task Complete !",
          icon: <Toast />,
          className: "order",
          style: {
            marginTop: "85vh",
          },
          duration: PICK_TOAST_MS / 1000,
        });
        setAllQty(
          (prevState) =>
            prevState +
            (pickedQuantityOverride ??
              toNumber(completedPickItem?.pickTask?.quantity))
        );

        markTaskCompleteInUi(id, isStatic);

        setOkayLoading(false);

        const releaseModalOpened = await openPutReleaseModalWhenOrderReady(
          completedPickItem
        );
        if (releaseModalOpened) {
          return;
        }

        try {
          if (orderRefId) {
            const order = await fetchWmsOrder(orderRefId);
            if (areOrderPicksDone(order)) {
              

              await wait(PICK_TOAST_MS);
              messageApi.open({
                content: `Order ${orderRefId} completed`,
                icon: <Toast />,
                className: "order",
                style: { marginTop: "85vh" },
                duration: ORDER_TOAST_MS / 1000,
              });
              // Wait for the order toast to display before unmounting state
              await wait(ORDER_TOAST_MS);
              completeOrderInUi(orderRefId);
            }
          }
        } catch {}
      }
    } catch (error: any) {
      setErrorMessage(error.response.data);
      setTargetItemsData((current: any) =>
        targetItemsData.map((obj: any, key: any) => {
          if (obj.id === id) {
            return {
              ...obj,
              okLoader: false,
            };
          }
          return obj;
        })
      );
      setIsOkPopup(true);
      setOkayLoading(false);
    } finally {
      await refreshFulfillmentOrders();
    }
  };

  const isOpenRestartPopup = () => {
    setIsRestartPopup(true);
  };

  const isCloseRestartPopup = () => {
    setIsRestartPopup(false);
  };

  const isOpenFlagPopup = () => {
    setIsFlagPopup(true);
  };

  const isCloseFlagPopup = () => {
    setIsFlagPopup(false);
  };

  const isOpenOkPopup = (e: any, taskId: string, Index: any, isStatic: boolean = false) => {
    e.stopPropagation();
    ohHandleOkItem(taskId, Index, isStatic);
  };

  const isCloseOkPopup = () => {
    //ohHandleOkItem(selectedOrder.id);
    setIsOkPopup(false);
  };
  const isCancelOkPopup = () => {
    setIsOkPopup(false);
  };

  const dateTimeFormat = (time: string) => {
    const distance = formatDistance(Date.now(), new Date(time), {
      addSuffix: true,
    });
    const firstWord = distance.replace(/in/g, "");
    const finalWord = firstWord.replace(/about/g, "");
    const formatted = finalWord.replace(/mutes/g, "Mins");
    const one = formatted.replace(/mute/g, "Minute");
    const two = one.replace(/ago/g, "");
    return two;
  };

  const onHandleOrderItem = async (orderItem: any, isStatic: boolean = false, selectedPartition?: string) => {
    const targetItemsData = isStatic ? staticItemsData : itemsData;
    const setTargetItemsData = isStatic ? setStaticItemsData : setItemsData;

    // Get the current item from state to ensure we have the latest selectedPartition
    const currentItem = targetItemsData.find((obj: any) => obj.id === orderItem[0].id);
    const itemToUse = currentItem || orderItem[0];
    
    // Use passed selectedPartition or fall back to item state
    const finalSelectedPartition = selectedPartition || itemToUse.selectedPartition;
    


    let skuDetails: any = [];
    const res: any = await axios.get(`/MasterData/sku/${itemToUse.pickTask.skuId}`, {
      headers: {
        Accept: "application/json",
        tenantId: 1,
      },
    });
    if (res.status === 200) {
      skuDetails.push(res.data);
    }

    setSelectedTaskId(itemToUse.id);
    setSelectedDate(itemToUse.creationDate);
    let newOrderQuantity = [];
    newOrderQuantity[itemToUse.pickTask.skuId] = 1;
    const requiredQty = toNumber(itemToUse.pickTask.quantity ?? itemToUse.qty);
    setQCount(requiredQty);

    setSelectedOrder({
      ...itemToUse,
      qty: requiredQty,
      selectedQty: requiredQty,
      skuDetails: skuDetails,
      isStatic: isStatic,
      selectedPartition: finalSelectedPartition
    });

    setIsSkuModalVisible(true);

    setTargetItemsData((current: any) =>
      targetItemsData.map((obj: any, key: any) => {
        if (obj.id === orderItem[0].id && obj.orderColor !== "completed") {
          if (obj.orderColor === "default") {
	            return {
	              ...obj,
	              orderColor: "progress",
	              qty: requiredQty,
	              selectedQty: requiredQty,
	              skuDetails: skuDetails,
	              selectedPartition: finalSelectedPartition // Use passed partition
	            };
          } else {
            return {
              ...obj,
              orderColor: "progress",
              skuDetails: skuDetails,
              selectedPartition: finalSelectedPartition // Use passed partition
            };
          }
	        } else {
	          if (obj.orderColor === "progress" && toNumber(obj.selectedQty) > 0) {
	            return {
	              ...obj,
	              orderColor: "hold",
            };
          } else {
            return {
              ...obj,
              orderColor:
                obj.orderColor === "" || obj.orderColor === "progress"
                  ? ""
                  : obj.orderColor,
            };
          }
        }
	      })
	    );
	  };
	  const updateSelectedQuantity = (nextQuantity: number) => {
	    if (!selectedOrder?.id) {
	      return;
	    }

	    const requiredQty = toNumber(selectedOrder.qty ?? selectedOrder.pickTask?.quantity);
	    const clampedQuantity = Math.max(0, Math.min(nextQuantity, requiredQty));
	    const isStaticOrder = selectedOrder?.isStatic === true;
	    const setTargetItemsData = isStaticOrder ? setStaticItemsData : setItemsData;

	    setQCount(clampedQuantity);
	    setState(clampedQuantity === requiredQty ? "completed" : "progress");
	    setSelectedOrder((current: any) =>
	      current?.id === selectedOrder.id
	        ? { ...current, selectedQty: clampedQuantity }
	        : current
	    );

	    setTargetItemsData((current: any[]) =>
	      current.map((obj: any) => {
	        if (obj.id !== selectedOrder.id) {
	          return obj;
	        }

	        return {
	          ...obj,
	          orderColor: clampedQuantity === requiredQty ? "completed" : "progress",
	          selectedQty: clampedQuantity,
	          selectedPartition: obj.selectedPartition,
	        };
	      })
	    );
	  };

	  const addQty = (qCount: number) => {
	    updateSelectedQuantity(qCount);
	  };

	  const removeQty = (qCount: number) => {
	    updateSelectedQuantity(qCount);
	  };

  const onHandleRestart = () => {
    window.location.reload();
  };

  const quantityNeededCount =
    qCount < 10 ? ("0" + `${qCount}`).slice(-2) : qCount;


  const handleFlagButtonClick = () => {
    setFlagStep("select");
    setFlagReason(null);
    setFlagRemarks("");
    setIsFlagModalVisible(true);
  };

  const handleFlagModalClose = () => {
    setIsFlagModalVisible(false);
    setFlagStep("select");
    setFlagReason(null);
    setFlagRemarks("");
  };

  const handleFlagSubmit = async () => {
    if (!selectedOrder || !flagReason) return;
    const remarks = flagReason === "damaged" ? "Damaged SKU-Bin" : "Wrong SKU-Bin";
    const pickTaskId = selectedOrder.id;
    const isStatic = selectedOrder.isStatic || false;
    const flaggedPickItem = selectedOrder;
    try {
      await axios.put(
        `/MasterData/sku/flag/${selectedOrder.pickTask.skuId}/true/${encodeURIComponent(remarks)}`,
        null,
        { headers: { Accept: "application/json", tenantId: 1 } }
      );
      await axios.put(`/PickStation/cancelPick/${pickTaskId}`, null, {
        headers: { Accept: "application/json", tenantId: 1 },
      });
      markTaskCompleteInUi(pickTaskId, isStatic);
      messageApi.success("Problem reported and flagged successfully");
      await openPutReleaseModalWhenOrderReady(flaggedPickItem);
    } catch (error) {
      messageApi.error("Failed to report problem");
    } finally {
      await refreshFulfillmentOrders();
    }
    handleFlagModalClose();
  };

  const openShortPickModal = () => {
    setShortPickReason(null);
    setIsShortPickModalVisible(true);
  };

  // Cancelling returns the operator to the pick modal with the quantity untouched, so
  // they can dial it back up to the full required quantity instead of short picking.
  const closeShortPickModal = () => {
    if (shortPickSubmitting) {
      return;
    }

    setIsShortPickModalVisible(false);
    setShortPickReason(null);
  };

  // A short pick is recorded, flagged and then signalled — in that order. The quantity
  // has to reach the task before it completes: updatePickTask rejects a complete task.
  const handleShortPickSubmit = async () => {
    const pickTaskId = selectedOrder?.id;
    const skuId = selectedOrder?.pickTask?.skuId;
    const remarks = getShortPickRemarks(shortPickReason);

    if (!pickTaskId || !skuId || !remarks || shortPickSubmitting) {
      return;
    }

    const shortPickItem = selectedOrder;
    const isStatic = selectedOrder?.isStatic === true;
    const pickedQuantity = toNumber(selectedOrder.selectedQty);

    setShortPickSubmitting(true);

    try {
      await axios.put(
        `/PickStation/updatePickTask/${encodeURIComponent(
          pickTaskId
        )}/${pickedQuantity}`,
        null,
        { headers: { Accept: "application/json", tenantId: 1 } }
      );
    } catch (error: any) {
      // Nothing has changed server side, so the modal stays open for a retry.
      messageApi.open({
        type: "error",
        content:
          typeof error?.response?.data === "string"
            ? error.response.data
            : "Failed to record the short pick",
        icon: <Toast />,
        className: "error",
        style: { marginTop: "85vh" },
      });
      setShortPickSubmitting(false);
      return;
    }

    // The quantity — the inventory-critical half — is already recorded. Flagging is
    // bookkeeping a supervisor can repair, so a failure here must not strand the
    // operator on a pick that can never complete.
    try {
      await axios.put(
        `/MasterData/sku/flag/${skuId}/true/${encodeURIComponent(remarks)}`,
        null,
        { headers: { Accept: "application/json", tenantId: 1 } }
      );
    } catch (_error) {
      messageApi.open({
        type: "error",
        content: "Short pick recorded, but flagging the SKU failed",
        icon: <Toast />,
        className: "error",
        style: { marginTop: "85vh" },
      });
    }

    setIsShortPickModalVisible(false);
    setShortPickReason(null);

    try {
      await ohHandleOkItem(pickTaskId, [shortPickItem], isStatic, pickedQuantity);
    } finally {
      setShortPickSubmitting(false);
    }
  };

  const handleDoneButtonClick = (event: any) => {
    if (!selectedOrder?.id) {
      return;
    }

    if (isShortPick(selectedOrder.selectedQty, selectedOrder.qty)) {
      openShortPickModal();
      return;
    }

    isOpenOkPopup(
      event,
      selectedOrder.id,
      [selectedOrder],
      selectedOrder.isStatic || false
    );
  };

  const handleOpenSkuModal = () => {
    setIsSkuModalVisible(true);
  };

  const handleCloseSkuModal = () => {
    // The short-pick modal sits on top of this one; closing the pick underneath it
    // would strand the operator mid-decision.
    if (isShortPickModalVisible) {
      return;
    }

    setIsSkuModalVisible(false);
    setSelectedOrder([]);
  };

  const handlePartitionSelect = (item: any, partition: string, isStatic: boolean = false) => {
    const targetItemsData = isStatic ? staticItemsData : itemsData;
    const setTargetItemsData = isStatic ? setStaticItemsData : setItemsData;

    setTargetItemsData((current: any) =>
      targetItemsData.map((obj: any) => {
        if (obj.id === item.id) {
          return {
            ...obj,
            selectedPartition: partition,
            orderColor: "progress"
          };
        }
        return obj;
      })
    );
  };

    const REGULAR_SLOT_CARD_HEIGHT = "280px";
    const STATIC_SLOT_CARD_HEIGHT = "180px";
    const STATIC_SLOT_CARD_WIDTH = "140px";

    const renderSlotWithPartitions = (item: any, binData: any, isStatic: boolean = false) => {
      // Simplify: if no binData, partitionCount is 0 or 1, treat as single partition
      const isMissingPartitionCount = !!binData && (binData.partitionCount === undefined || binData.partitionCount === null);
      const isSingleBySkuZero = isMissingPartitionCount && Array.isArray(binData?.skus) && binData.skus.length > 0 && String(binData.skus[0]?.partition) === "0";
      const partitionCount = (isStatic || !binData || binData.partitionCount === 0 || binData.partitionCount === 1 || isSingleBySkuZero) ? 0 : binData.partitionCount;
      const skuData = binData?.skus?.find((sku: any) => sku.skuId === item.pickTask.skuId);
      const itemPartition = (partitionCount === 0 || isStatic) ? "1" : skuData?.partition;


      const handlePartitionClick = (partitionNum: string) => {
        // For single partition (partitionCount 0) or static items, always allow click
        // For multi-partition, check if this partition contains the target SKU
        if (partitionCount === 0 || isStatic || itemPartition === partitionNum) {
          handlePartitionSelect(item, partitionNum, isStatic);
          // Pass partition directly to avoid state timing issues
          setTimeout(() => {
            onHandleOrderItem([item], isStatic, partitionNum);
          }, 10);
        }
      };

      // Handle single partition
      const effectivePartitionCount = partitionCount === 0 ? 1 : partitionCount;

      // Units this pick task asks for, shown on the slot so the operator does not have
      // to open the pick modal to find out how many to take.
      const slotQuantity = toNumber(item?.pickTask?.quantity);

      const groupKey = getGateColorKeyForPickItem(item);
      const knownGroup = Object.prototype.hasOwnProperty.call(GroupColor, groupKey);
      const activeOrderId = getActiveOrderId();
      const isActiveOrder = String(item?.pickTask?.orderReferenceId ?? "") === String(activeOrderId ?? "");
      const groupColor = knownGroup
        ? GroupColor[groupKey]
        : (!isPutWallEnabled && isActiveOrder ? DefaultHighlightColor : undefined);
            let slotBackgroundColor = groupColor?.bg || "#e8e8e8";
            let borderColor = groupColor?.border || "#b5b5b5";

      // Get coordinates for display
      const coordinates = isStatic 
        ? item.staticPosition || `${item.pickTask.gate.x},${item.pickTask.gate.y},${item.pickTask.gate.z}`
        : `${item.pickTask.gate.x},${item.pickTask.gate.y},${item.pickTask.gate.z}`;

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: isStatic ? STATIC_SLOT_CARD_HEIGHT : REGULAR_SLOT_CARD_HEIGHT,
            border: `2px solid ${borderColor}`,
            borderRadius: "8px",
            backgroundColor: slotBackgroundColor
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: effectivePartitionCount === 1 ? "1fr" : effectivePartitionCount === 2 ? "1fr 1fr" : "1fr 1fr",
              gridTemplateRows: effectivePartitionCount === 1 ? "1fr" : effectivePartitionCount === 2 ? "1fr" : "1fr 1fr",
              gap: effectivePartitionCount === 1 ? "0px" : "2px",
              flex: 1,
              padding: "4px"
            }}
          >
          {Array.from({ length: effectivePartitionCount }, (_, i) => {
            let partitionNum: string;
            if (effectivePartitionCount === 1) {
              partitionNum = "1";
            } else if (effectivePartitionCount === 2) {
              const twoPartitionOrder = [2, 1];
              partitionNum = twoPartitionOrder[i].toString();
            } else if (effectivePartitionCount === 4) {
              const fourPartitionOrder = [4, 1, 3, 2];
              partitionNum = fourPartitionOrder[i].toString();
            } else {
              partitionNum = (i + 1).toString();
            }
            
            const isItemPartition = partitionCount === 0 ? true : (itemPartition === partitionNum);
            const isSelected = item.selectedPartition === partitionNum;
            const isCompleted = partitionCount === 0 ? 
              (item.orderColor === "completed") : 
              (item.orderColor === "completed" && isSelected);
              
            // Simplified logic: if single partition OR if this partition contains the item
            const shouldShowPartition = partitionCount === 0 || isStatic || isItemPartition;
              


            let backgroundColor = "#b7b7b7";
            let partitionBorderColor = "#9a9a9a";
            
            if (shouldShowPartition) {
              if (isCompleted) {
                backgroundColor = "#52c41a";
                partitionBorderColor = "#52c41a";
              } else if (isSelected) {
                backgroundColor = "#1890ff";
                partitionBorderColor = "#1890ff";
              } else {
                // Default: let the group color of the slot show through
                backgroundColor = "transparent";
                partitionBorderColor = "transparent";
              }
            } else {
              // Hidden partitions should not override slot background
              backgroundColor = "transparent";
              partitionBorderColor = "transparent";
            }

            return (
              <div
                key={partitionNum}
                style={{
                  backgroundColor,
                  border: `2px solid ${partitionBorderColor}`,
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: shouldShowPartition && !isCompleted ? "pointer" : "default",
                  transition: "all 0.2s ease"
                }}
                onClick={() => {
                  if (shouldShowPartition && !isCompleted) {
                    handlePartitionClick(partitionNum);
                  }
                }}
                onMouseEnter={(e) => {
                  if (shouldShowPartition && !isCompleted) {
                    e.currentTarget.style.opacity = "0.9";
                  }
                }}
                onMouseLeave={(e) => {
                  if (shouldShowPartition && !isCompleted) {
                    e.currentTarget.style.opacity = "1";
                  }
                }}
              >
                {isCompleted ? (
                  <span style={{ color: "white", fontSize: "20px" }}>✓</span>
                ) : shouldShowPartition ? (
                  // Quantity only — the partition number told the operator nothing extra, since
                  // the SKU's partition is already the only cell in the grid that renders.
                  <span style={{
                    color: "#333",
                    fontSize: isStatic ? "13px" : "16px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}>
                    Qty {slotQuantity}
                  </span>
                ) : null}
              </div>
            );
          })}
          </div>

          <div
            style={{
              padding: "6px 8px",
              backgroundColor: "#d9d9d9",
              borderTop: "1px solid #c6c6c6",
              textAlign: "center",
              fontSize: "12px",
              fontWeight: "bold",
              color: "#333",
              borderBottomLeftRadius: "6px",
              borderBottomRightRadius: "6px"
            }}
          >
            Bin - {formatSlotDisplay(coordinates)}
          </div>
        </div>
      );
    };

  const renderEmptySlot = (label: string, isStatic: boolean = false) => (
    <div
      style={{
        width: "100%",
        height: isStatic ? STATIC_SLOT_CARD_HEIGHT : REGULAR_SLOT_CARD_HEIGHT,
        backgroundColor: "#f5f5f5",
        border: "2px dashed #d9d9d9",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ flex: 1 }} />
      <div
        style={{
          padding: "6px 8px",
          backgroundColor: "#e9e9e9",
          borderTop: "1px solid #d3d3d3",
          textAlign: "center",
          fontSize: "12px",
          fontWeight: "bold",
          color: "#666",
          borderBottomLeftRadius: "6px",
          borderBottomRightRadius: "6px",
        }}
      >
        Bin - {formatSlotDisplay(label)}
      </div>
	    </div>
	  );

  const fallbackHeaderOrderIds = useMemo(() => {
    const orderIds = new Set<string>();
    pickTaskItems.forEach((task: any) => {
      const orderReferenceId = String(task?.pickTask?.orderReferenceId ?? "").trim();
      if (orderReferenceId) {
        orderIds.add(orderReferenceId);
      }
    });
    fulfillmentOrders.forEach((order: any) => {
      const orderReferenceId = getOrderReferenceId(order);
      if (orderReferenceId && !areOrderPicksDone(order)) {
        orderIds.add(orderReferenceId);
      }
    });
    return Array.from(orderIds).slice(0, 4);
  }, [fulfillmentOrders, pickTaskItems]);

	  return (
    <>
      <Styles.Container className="order-detail">
        <>
          <Styles.TaskBar>
            <Styles.TaskBarInner>
	              {enablePutWallFeature && sortedPutWallGates.length > 0 ? (
	                <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
	                  <Styles.MappingTasksContainer>
	                    {sortedPutWallGates.map((gate: PutWallGateState) => {
	                      const gateKey = normalizePutGate(gate.location);
	                      const mappingTask = visibleMappingTasksByGate.get(gateKey);
	                      const mapping = mappingTask?.orderToteMappingTask;
	                      const currentOrder = String(gate.currentOrder ?? "").trim();
	                      const orderReferenceId = currentOrder || String(mapping?.orderReferenceId ?? "").trim();
	                      const gateColor = GroupColor[gateKey] || {
	                        bg: "#e8e8e8",
	                        border: "#b5b5b5",
	                        text: "#333",
	                      };
	                      const isDisabled = gate.isDisabled === true;
	                      const isPendingMapping = !!mappingTask;
	                      const gateTaskState = isDisabled
	                        ? "disabled"
	                        : isPendingMapping
	                          ? "actionRequired"
	                          : currentOrder
	                            ? "mapped"
	                            : "empty";
	                      const label = (() => {
	                        if (isDisabled) {
	                          return "Disabled Gate";
	                        }
	                        if (isPendingMapping) {
	                          return "Order Tote Mapping Required";
	                        }
	                        if (currentOrder) {
	                          return "Order Tote Mapping Done";
	                        }
	                        return "Empty Gate";
	                      })();
	                      const orderDisplay = isPendingMapping
	                        ? orderReferenceId || "Assign tote"
	                        : currentOrder
	                          ? currentOrder
	                          : "Empty";
	                      const toteId = gate.orderToteId || mapping?.orderToteId;
	                      const backgroundColor =
	                        isDisabled || gateTaskState === "empty" ? "#f5f5f5" : gateColor.bg;
	                      const borderColor =
	                        isDisabled || gateTaskState === "empty" ? "#d9d9d9" : gateColor.border;
	                      const textColor =
	                        isDisabled
	                          ? "#999"
	                          : gateTaskState === "empty"
	                            ? "#666"
	                            : gateColor.text;
	                      const metaText = isPendingMapping
	                        ? `${formatPutGateLabel(gate.location)} / Assign Tote`
	                        : `${formatPutGateLabel(gate.location)}${toteId ? ` / ${toteId}` : ""}`;

	                      return (
	                        <Styles.MappingTaskCard
	                          key={`${gate.putWallId || defaultPutWallId}-${gateKey || gate.location}`}
	                          type="button"
	                          $state={gateTaskState}
	                          tabIndex={isPendingMapping ? 0 : -1}
	                          style={{
	                            backgroundColor,
	                            borderColor,
	                            color: textColor,
	                          }}
	                          onClick={
	                            isPendingMapping
	                              ? () => openOrderToteMappingModal(mappingTask)
	                              : undefined
	                          }
	                        >
	                          <Styles.MappingTaskIcon
	                            $state={gateTaskState}
	                            style={{
	                              backgroundColor:
	                                gateTaskState === "empty" || isDisabled
	                                  ? "#e6e6e6"
	                                  : gateColor.bg,
	                            }}
	                          >
	                            <ProgressInfoIcon />
	                          </Styles.MappingTaskIcon>
	                          <Styles.MappingTaskContent>
	                            <Styles.MappingTaskLabel>
	                              {label}
	                            </Styles.MappingTaskLabel>
	                            <Styles.MappingTaskOrder
	                              title={orderDisplay}
	                            >
	                              {orderDisplay}
	                            </Styles.MappingTaskOrder>
	                          </Styles.MappingTaskContent>
	                          <Styles.MappingTaskMeta>
	                            {metaText}
	                          </Styles.MappingTaskMeta>
	                        </Styles.MappingTaskCard>
	                      );
	                    })}
		                  </Styles.MappingTasksContainer>
	                </div>
	              ) : enableOrderToteMapping && visibleOrderToteMappingTasks.length > 0 ? (
	                <Styles.MappingTasksContainer>
	                  {visibleOrderToteMappingTasks.map((task: any) => {
	                    const mapping = task.orderToteMappingTask;
	                    return (
	                      <Styles.MappingTaskCard
	                        key={task.id}
	                        type="button"
	                        $state="actionRequired"
	                        onClick={() => openOrderToteMappingModal(task)}
	                      >
	                        <Styles.MappingTaskIcon $state="actionRequired">
	                          <ProgressInfoIcon />
	                        </Styles.MappingTaskIcon>
	                        <Styles.MappingTaskContent>
	                          <Styles.MappingTaskLabel>
	                            Order Tote Mapping Required
	                          </Styles.MappingTaskLabel>
	                          <Styles.MappingTaskOrder title={mapping?.orderReferenceId}>
	                            {mapping?.orderReferenceId}
	                          </Styles.MappingTaskOrder>
	                        </Styles.MappingTaskContent>
	                        <Styles.MappingTaskMeta>
	                          {formatPutGateLabel(mapping?.gate) || "Pending"}
	                        </Styles.MappingTaskMeta>
	                      </Styles.MappingTaskCard>
	                    );
	                  })}
	                </Styles.MappingTasksContainer>
	              ) : fallbackHeaderOrderIds.length > 0 ? (
	                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
	                  {fallbackHeaderOrderIds.map((orderId: string) => {
	                    const taskForOrder = pickTaskItems.find(
	                      (p: any) => p?.pickTask?.orderReferenceId === orderId
	                    );
	                    const groupKey = getGateColorKeyForPickItem(taskForOrder);
	                    const color = GroupColor[groupKey] || { bg: "#e8e8e8", border: "#b5b5b5", text: "#333" };
	                    const isActive = getActiveOrderId() === orderId;
	                    return (
	                      <div key={orderId} style={{ backgroundColor: color.bg, border: `2px solid ${color.border}`, color: color.text, padding: "4px 8px", borderRadius: 8, fontWeight: isActive ? 700 : 500, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={orderId}>
	                        {orderId}
	                      </div>
	                    );
	                  })}
	                </div>
	              ) : (
	                <Styles.Item>
	                  <span>Orders:</span>
	                  <span>—</span>
	                </Styles.Item>
	              )}

            </Styles.TaskBarInner>

          </Styles.TaskBar>
          <Styles.Content>
            <Styles.OrderDetail >
              <Styles.OrderContainer>
                <Styles.Head>
                  {selectedOrder?.length === 0 ? (
                    <span>Pickup Items</span>
                  ) : (
                    <>
                      <span>Pickup Item:</span>&nbsp;
                      <span>
                        {selectedOrder !== null && selectedOrder.pickTask.sku}
                      </span>
                    </>
                  )}
                </Styles.Head>
                <Styles.SlotsContainer>
                  {firstRow.length > 0 && (
                    <Styles.SlotsRow columns={firstRow.length || 1}>
                      {firstRow.map((val: any) => {
                        const slotItems = itemsData.filter(
                          (item: any) => item.position === val
                        );
                        const visibleSlotItem = getVisibleSlotItem(slotItems);
                        return (
                          <div
                            key={`first-${val}`}
                            style={{ width: "100%", display: "flex" }}
                          >
                            {visibleSlotItem
                              ? renderSlotWithPartitions(
                                  visibleSlotItem,
                                  binPartitionData[visibleSlotItem.pickTask.binId],
                                  false
                                )
                              : renderEmptySlot(val)}
                          </div>
                        );
                      })}
                    </Styles.SlotsRow>
                  )}
                  {secondRow.length > 0 && (
                    <Styles.SlotsRow columns={secondRow.length || 1}>
                  {secondRow.map((val: any) => {
                      const slotItems = itemsData
                        .slice()
                        .reverse()
                        .filter((item: any) => item.position === val);
                      const visibleSlotItem = getVisibleSlotItem(slotItems);
                      return (
                        <div
                          key={`second-${val}`}
                          style={{ width: "100%", display: "flex" }}
                        >
                          {visibleSlotItem
                            ? renderSlotWithPartitions(
                                visibleSlotItem,
                                binPartitionData[visibleSlotItem.pickTask.binId],
                                false
                              )
                            : renderEmptySlot(val)}
                        </div>
                      );
                    })}
                    </Styles.SlotsRow>
                  )}
                </Styles.SlotsContainer>
              </Styles.OrderContainer>

              {/* Add Static Pickwall Section when configured */}
              {staticSlots.length > 0 && (
                <Styles.OrderContainer>
                  <Styles.Head>
                    <span>Static Pickwall</span>
                  </Styles.Head>
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      width: "100%",
                      flexWrap: "nowrap",
                      justifyContent: "flex-start",
                      padding: "0 12px",
                      overflowX: "auto",
                      overflowY: "hidden"
                    }}
                  >
                    {staticSlots.map((slot: any) => {
                      const Index = staticItemsData.filter(
                        (item: any) => item.staticSlotId === slot.id
                      );
                      const visibleStaticItem = getVisibleSlotItem(Index);

                      return (
                        <div
                          key={slot.id}
                          style={{
                            flex: `0 0 ${STATIC_SLOT_CARD_WIDTH}`,
                            width: STATIC_SLOT_CARD_WIDTH,
                            minWidth: STATIC_SLOT_CARD_WIDTH,
                          }}
                        >
                          {visibleStaticItem ? (
                            renderSlotWithPartitions(visibleStaticItem, binPartitionData[visibleStaticItem.pickTask.binId], true)
                          ) : (
                            renderEmptySlot(slot, true)
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Styles.OrderContainer>
              )}


            </Styles.OrderDetail>
          </Styles.Content>
          <RestartPopup
            visible={isRestartPopup}
            isCloseRestartPopup={isCloseRestartPopup}
          />
          <FlagPopup
            visible={isFlagPopup}
            isCloseFlagPopup={isCloseFlagPopup}
          />
          <OkPopup
            visible={isOkPopup}
            isCloseOkPopup={isCloseOkPopup}
            isCancel={isCancelOkPopup}
            errorMessage={errorMessage}
          />
        </>
      </Styles.Container>
      <>{contextHolder}</>

      <Modal
        title=""
        visible={!!selectedMappingTask}
        onCancel={closeOrderToteMappingModal}
        width={560}
        footer={null}
        centered
      >
        {selectedMappingTask && (
          <Styles.MappingModalBody>
            <Styles.MappingModalTitle>
              Initialize Order Tote
            </Styles.MappingModalTitle>
            <Styles.MappingModalOrder>
              {selectedMappingTask.orderToteMappingTask?.orderReferenceId}
            </Styles.MappingModalOrder>

            <Styles.MappingDirective>
              <Styles.MappingDirectiveLabel>
                Put Wall Gate
              </Styles.MappingDirectiveLabel>
              <Styles.MappingDirectiveText>
                <strong>
                  {formatPutGateLabel(
                    selectedMappingTask.orderToteMappingTask?.gate
                  ) || "—"}
                </strong>
              </Styles.MappingDirectiveText>
            </Styles.MappingDirective>

            <Styles.MappingField>
              <label>Order Tote ID</label>
              <Input
                ref={mappingToteInputRef}
                inputMode={resolveToteInputMode(mappingToteFieldTapped)}
                value={mappingOrderToteId}
                placeholder="TOTE-XXXXX"
                onChange={(event) => setMappingOrderToteId(event.target.value)}
                onClick={handleMappingToteFieldTap}
                disabled={mappingLoading}
              />
            </Styles.MappingField>

            <Styles.MappingModalFooter>
              <Button onClick={closeOrderToteMappingModal} disabled={mappingLoading}>
                Cancel
              </Button>
              <Button
                type="primary"
                loading={mappingLoading}
                onClick={handleInitializeOrderToteMapping}
              >
                Initialize
              </Button>
            </Styles.MappingModalFooter>
          </Styles.MappingModalBody>
        )}
      </Modal>

      {putReleaseTasks.map((putReleaseTask: any, index: number) => (
        <Modal
          key={putReleaseTask.id}
          title=""
          visible
          width={560}
          footer={null}
          centered
          closable={false}
          maskClosable={false}
          keyboard={false}
          zIndex={PUT_RELEASE_MODAL_Z_INDEX + index}
        >
          <Styles.PutReleaseModalBody>
            <Styles.PutReleaseCheck>✓</Styles.PutReleaseCheck>
            <Styles.PutReleaseTitle>Order Complete</Styles.PutReleaseTitle>
            <Styles.PutReleaseOrder>
              {putReleaseTask.putTask?.orderReferenceId}
            </Styles.PutReleaseOrder>

            <Styles.MappingDirective>
              <Styles.MappingDirectiveLabel>
                Action: Remove From Wall
              </Styles.MappingDirectiveLabel>
              <Styles.PutReleaseGate>
                {formatPutGateLabel(putReleaseTask.putTask?.gate)}
              </Styles.PutReleaseGate>
            </Styles.MappingDirective>

            <Styles.MappingModalFooter>
              <Button
                type="primary"
                loading={isPutReleaseTaskLoading(putReleaseTask.id)}
                onClick={() => handleConfirmPutRelease(putReleaseTask)}
              >
                Confirm Release
              </Button>
            </Styles.MappingModalFooter>
          </Styles.PutReleaseModalBody>
        </Modal>
      ))}

      <Modal
        title=""
        open={isSkuModalVisible && !!selectedOrder?.skuDetails?.length}
        onCancel={handleCloseSkuModal}
        width={920}
        footer={null}
      >
        {selectedOrder && selectedOrder.skuDetails && selectedOrder.skuDetails.length > 0 && (
          (() => {
            const enablePutWall = isPutWallEnabled;
            const gates = enablePutWall ? ((putWallGates || []) as any[]) : [];
            const gateMap: Record<string, any> = enablePutWall
              ? gates.reduce((acc: any, g: any) => {
                  const key = String(g?.location ?? "");
                  acc[key] = g;
                  return acc;
                }, {})
              : {} as any;
            const gridOrder = [["2", "1"], ["3", "4"]];
            const getGate = (loc: string) => enablePutWall ? gateMap[String(loc)] : undefined;

            // Determine older state colors based on quantity match
            const modalSelectedQty = toNumber(selectedOrder.selectedQty);
            const modalRequiredQty = toNumber(selectedOrder.qty);
            const isQtyMatched = modalSelectedQty === modalRequiredQty;
            const stateBgColor = isQtyMatched ? "#d4edda" : "#fff3cd";
            const stateBorderColor = isQtyMatched ? "#28a745" : "#856404";

            const renderCell = (location: string) => {
              const isCurrent =
                getGateColorKeyForPickItem(selectedOrder) === String(location);
              const hasTask = isCurrent;
              const color = hasTask ? GroupColor[location] : undefined;
              const disabled = getGate(location)?.isDisabled === true;
              const bg = disabled ? "#f5f5f5" : (color?.bg || "#e8e8e8");
              const border = disabled ? "#d9d9d9" : (color?.border || "#b5b5b5");
              const text = disabled ? "#999" : (color?.text || "#333");
              const showPulse = isCurrent && ackPutWall !== location;
              const fullOrderLabel = isCurrent ? (selectedOrder?.pickTask?.orderReferenceId ?? "") : "Empty";
              const orderLabel = fullOrderLabel.length > 50 ? fullOrderLabel.substring(0, 50) + "..." : fullOrderLabel;
              return (
                <div key={location} style={{ backgroundColor: bg, border: `2px solid ${border}`, borderRadius: 8, height: 90, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", animation: showPulse ? "pulseRing 1.2s ease-in-out infinite" : undefined, cursor: isCurrent ? "pointer" : "default" }} onClick={() => { if (isCurrent) setAckPutWall(location); }}>
                  <div style={{ position: "absolute", top: 6, right: 8, color: text, fontWeight: 700 }}>{location}</div>
                  <div style={{ color: text, fontWeight: 700, padding: "8px", textAlign: "center", fontSize: "12px", lineHeight: "1.2", wordBreak: "break-word", overflow: "hidden" }}>{orderLabel}</div>
                </div>
              );
            };
            return (
              <div style={{ display: "grid", gridTemplateColumns: "300px 1fr 240px", gap: 24 }}>
                {/* Left Column */}
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  {/* Header */}
                  <div style={{ fontSize: 20, fontWeight: 700, textAlign: "center", marginBottom: 16, color: "#333" }}>
                    Pick
                  </div>
                  {/* Top: Product Info and Bin/Partition Selector */}
                  <div>
                    <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
                      {selectedOrder.skuDetails[0].productName || "Industrial Widget"}
                    </div>
                    <div style={{ fontSize: 12, color: "#666", lineHeight: 1.6, marginBottom: 16 }}>
                      {(() => {
                        const modalBinData = binPartitionData[selectedOrder.pickTask.binId];
                        const modalPartitionCount = modalBinData?.partitionCount || 0;
                        let partText = "";
                        if (modalPartitionCount === 0) partText = "1";
                        else partText = modalBinData?.skus?.find((s: any) => s.skuId === selectedOrder.pickTask.skuId)?.partition || "N/A";
                        return (
                          <>
                            <div>Partition: {partText}</div>
                            <div>Order ID: {selectedOrder.pickTask.orderReferenceId}</div>
                            <div>SKU ID: {selectedOrder.pickTask.skuId}</div>
                            <div>Batch ID: {selectedOrder?.pickTask?.batchId || selectedOrder?.batchId || "--"}</div>
                          </>
                        );
                      })()}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", marginBottom: 24 }}>
                      <div style={{ height: "188px", width: "140px", margin: "0 auto", borderRadius: 10, background: stateBgColor, border: `4px solid ${stateBorderColor}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <div style={{ textAlign: "center", color: "#666", fontSize: 14, fontWeight: 600 }}>
                          Selected<br/>Bin/Partition
                        </div>
                      </div>
                      <div style={{ textAlign: "center", marginTop: 8, color: "#666", fontSize: 12 }}>Selected Bin/Partition</div>
                    </div>
                  </div>

                  {/* Bottom: Quantity Control */}
                  <div style={{ background: stateBgColor, borderRadius: 8, padding: 8, border: `1px solid ${stateBorderColor}`, marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, textAlign: "center", marginBottom: 12 }}>PICKUP QUANTITY</div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
	                      <Button size="large" disabled={toNumber(selectedOrder.selectedQty) === 0} onClick={() => removeQty(toNumber(selectedOrder.selectedQty) - 1)} style={{ width: 48, height: 48, fontSize: 22 }}>-</Button>
	                      <div style={{ fontSize: 36, fontWeight: 800 }}>
	                        {formatQuantity(selectedOrder.selectedQty)}/{formatQuantity(selectedOrder.qty)}
	                      </div>
	                      <Button size="large" onClick={() => addQty(toNumber(selectedOrder.selectedQty) + 1)} disabled={toNumber(selectedOrder.selectedQty) === toNumber(selectedOrder.qty)} style={{ width: 48, height: 48, fontSize: 22 }}>+</Button>
                    </div>
                  </div>
                </div>

                {/* Middle Column - SKU Image */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ width: "100%", height: 360, background: "#d9d9d9", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
                    {selectedOrder.skuDetails[0]?.imageUri && selectedOrder.skuDetails[0]?.imageUri !== "" ? (
                      <img src={selectedOrder.skuDetails[0]?.imageUri} alt="product image" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    ) : (
                      <ImageIcon style={{ width: "100%", height: "100%" }} />
                    )}
                  </div>
                  <div style={{ color: "#777", fontSize: 12, marginBottom: 16 }}>SKU Image</div>
                  {(() => {
                    const orderIdText = String(selectedOrder.pickTask.orderReferenceId ?? "").trim();
                    if (!orderIdText || !BARCODE_BASE_URL) return null;
                    const barcodeHeight = 100;
                    const barcodeWidth = 260;
                    const barcodeType = "CODE_128";
                    const base = BARCODE_BASE_URL.replace(/\/?$/, "");
                    const barcodeUrl = `${base}/${encodeURIComponent(orderIdText)}/${barcodeHeight}/${barcodeWidth}/${encodeURIComponent(barcodeType)}`;
                    return (
                      <div style={{ marginTop: 8, marginBottom: 16, textAlign: "center" }}>
                        <AuthenticatedImage
                          src={barcodeUrl}
                          scope={printerApiScope}
                          alt={`Barcode for ${orderIdText}`}
                          style={{ width: "100%", maxWidth: 260, height: 100, objectFit: "contain", background: "#fff", padding: 4, borderRadius: 4, border: "1px solid #eee" }}
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      </div>
                    );
                  })()}
                  
                  {enablePutWall && (
                    <div style={{ fontSize: 64, lineHeight: 1 }}>→</div>
                  )}
                </div>

                {/* Right Column */}
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                  {enablePutWall && (
                    <>
                      {/* Header */}
                      <div style={{ fontSize: 20, fontWeight: 700, textAlign: "center", marginBottom: 16, color: "#333" }}>
                        Put
                      </div>
                      {/* Top: Put Wall Grid */}
                      <div>
                        <div style={{ display: "flex", flexDirection: "column", marginTop: 90 }}>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 18, height: "188px" }}>
                            {gridOrder.flat().map((loc) => renderCell(loc))}
                          </div>
                          <div style={{ textAlign: "center", color: "#666", fontSize: 12 }}>Put Wall Location</div>
                          <style>
                            {`@keyframes pulseRing { 0% { box-shadow: 0 0 0 0 rgba(24, 144, 255, 0.5); } 70% { box-shadow: 0 0 0 8px rgba(24, 144, 255, 0); } 100% { box-shadow: 0 0 0 0 rgba(24, 144, 255, 0); } }`}
                          </style>
                        </div>
                      </div>
                    </>
                  )}

                  {!enablePutWall && (
                    <>
                      <div style={{ fontSize: 20, fontWeight: 700, textAlign: "center", marginBottom: 16, color: "#999" }}>
                        Put
                      </div>
                      <div>
                        <div style={{ display: "flex", flexDirection: "column", marginTop: 90 }}>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 18, height: "188px", filter: "grayscale(100%)", opacity: 0.5 }}>
                            {gridOrder.flat().map((loc) => (
                              <div key={loc} style={{ backgroundColor: "#f5f5f5", border: "2px solid #d9d9d9", borderRadius: 8, height: "90px", display: "flex", alignItems: "center", justifyContent: "center", color: "#999", fontWeight: 700 }}>{loc}</div>
                            ))}
                          </div>
                          <div style={{ textAlign: "center", color: "#999", fontSize: 12 }}>Put Wall Disabled</div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Bottom: Action Buttons - aligned with pickup quantity */}
                  <div>
	                    <Button type="primary" disabled={selectedOrder.completedStatus === true} onClick={(e) => { e.stopPropagation(); handleDoneButtonClick(e); }} style={{ width: "100%", height: 48, marginBottom: 12 }}>
                      {selectedOrder.okLoader === true ? <LoadIcon /> : "Done"}
                    </Button>
                    {enableReportProblem && (
                    <Button
                      onClick={handleFlagButtonClick}
                      disabled={selectedOrder.completedStatus === true}
                      style={{
                        width: "100%",
                        height: 44,
                        backgroundColor: "#FFA500",
                        borderColor: "#FFA500",
                        color: "white",
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      <span style={{ marginRight: 6 }}>&#9888;</span> Report a Problem
                    </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()
        )}
      </Modal>

      <Modal
        title="⚠️ Report a Problem"
        open={isFlagModalVisible}
        onCancel={handleFlagModalClose}
        footer={null}
        centered
      >
        {flagStep === "select" && (
          <div style={{ padding: "16px 0" }}>
            <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>
              Select a reason for flagging:
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Button
                size="large"
                type={flagReason === "damaged" ? "primary" : "default"}
                onClick={() => setFlagReason("damaged")}
                style={{
                  height: 56,
                  fontSize: 16,
                  fontWeight: 600,
                  ...(flagReason === "damaged" ? { backgroundColor: "#FFA500", borderColor: "#FFA500" } : {}),
                }}
              >
                Damaged SKU / Bin
              </Button>
              <Button
                size="large"
                type={flagReason === "wrong" ? "primary" : "default"}
                onClick={() => setFlagReason("wrong")}
                style={{
                  height: 56,
                  fontSize: 16,
                  fontWeight: 600,
                  ...(flagReason === "wrong" ? { backgroundColor: "#FFA500", borderColor: "#FFA500" } : {}),
                }}
              >
                Wrong SKU / Bin
              </Button>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
              <Button onClick={handleFlagModalClose} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button
                type="primary"
                disabled={!flagReason}
                onClick={() => setFlagStep("confirm")}
              >
                Next
              </Button>
            </div>
          </div>
        )}
        {flagStep === "confirm" && (
          <div style={{ padding: "16px 0", textAlign: "center" }}>
            <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
              Confirm Submission
            </p>
            <p style={{ fontSize: 14, color: "#666", marginBottom: 24 }}>
              You are flagging this item as:{" "}
              <strong>{flagReason === "damaged" ? "Damaged SKU / Bin" : "Wrong SKU / Bin"}</strong>
            </p>
            <p style={{ fontSize: 14, color: "#666", marginBottom: 24 }}>
              Bin: <strong>{selectedOrder?.pickTask?.binId}</strong> &nbsp;|&nbsp; SKU: <strong>{selectedOrder?.pickTask?.skuId}</strong>
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
              <Button
                size="large"
                onClick={() => setFlagStep("select")}
                style={{ width: 120, height: 48 }}
              >
                No
              </Button>
              <Button
                size="large"
                type="primary"
                danger
                onClick={handleFlagSubmit}
                style={{ width: 120, height: 48 }}
              >
                Yes
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Short Pick"
        open={isShortPickModalVisible && !!selectedOrder?.id}
        onCancel={closeShortPickModal}
        footer={null}
        centered
        maskClosable={!shortPickSubmitting}
        closable={!shortPickSubmitting}
        zIndex={SHORT_PICK_MODAL_Z_INDEX}
      >
        <div style={{ padding: "16px 0" }}>
          <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            Picking {formatQuantity(selectedOrder?.selectedQty)} of{" "}
            {formatQuantity(selectedOrder?.qty)} — select a reason:
          </p>
          <p style={{ fontSize: 14, color: "#666", marginBottom: 20 }}>
            SKU: <strong>{selectedOrder?.pickTask?.skuId}</strong> &nbsp;|&nbsp; Bin:{" "}
            <strong>{selectedOrder?.pickTask?.binId}</strong>
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {SHORT_PICK_REASONS.map((reason) => (
              <Button
                key={reason.value}
                size="large"
                type={shortPickReason === reason.value ? "primary" : "default"}
                disabled={shortPickSubmitting}
                onClick={() => setShortPickReason(reason.value)}
                style={{
                  height: 56,
                  fontSize: 16,
                  fontWeight: 600,
                  ...(shortPickReason === reason.value
                    ? { backgroundColor: "#FFA500", borderColor: "#FFA500" }
                    : {}),
                }}
              >
                {reason.label}
              </Button>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
            <Button
              onClick={closeShortPickModal}
              disabled={shortPickSubmitting}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              disabled={!shortPickReason}
              loading={shortPickSubmitting}
              onClick={handleShortPickSubmit}
            >
              Confirm Short Pick
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
